// src/components/Navbar.tsx
import { useState, useEffect } from 'react';
import { Coffee, Menu, X, ArrowUpRight, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Track scrolling for header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => document.body.classList.remove('no-scroll');
  }, [isMobileMenuOpen]);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const navLinks = [
    { name: 'Home', href: '/', id: 'home' },
    { name: 'About', href: '/about', id: 'about' },
    { name: 'Menu', href: '/menu', id: 'menu' },
    { name: 'Contact', href: '/contact', id: 'contact' },
  ];

  return (
    <>
      {/* Fixed Navbar */}
      <header
        className={`fixed top-1 left-0 right-0 z-50 transition-all duration-300 rounded-full mx-2 ${
          isScrolled ? 'glass py-3 shadow-sm' : 'bg-transparent py-3'
        }`}
      >
        <div className="container-custom flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center bg-coffee-800/80 backdrop-blur-sm px-3 py-1 rounded-full"
          >
            <Coffee className="h-8 w-8 text-coffee-300 mr-2" />
            <span className="text-xl font-serif font-semibold text-white">
              moca
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`text-sm font-sans font-medium bg-coffee-800/80 backdrop-blur-sm px-3 py-1 rounded-full transition-all duration-300 hover:bg-coffee-800 ${
                  location.pathname === link.href
                    ? 'text-white underline underline-offset-4'
                    : 'text-coffee-200 hover:text-white hover:underline hover:underline-offset-4'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA Button */}
          <div className="hidden md:block">
            <Link
              to="/menu"
              className="btn-primary-rounded text-sm font-sans font-medium flex items-center space-x-1"
            >
              <span>Shop Now</span>
              <ArrowUpRight size={16} />
            </Link>
          </div>

          {/* Mobile Menu Controls */}
          <div className="flex items-center space-x-3 md:hidden">
            {/* Phone CTA */}
            <a
              href="tel:(555) 123-4567"
              className="flex items-center justify-center w-10 h-10 rounded-full bg-coffee-600 text-white transition-all duration-300 hover:scale-105 transform"
            >
              <Phone size={20} />
              <span className="sr-only">Call Us</span>
            </a>
            {/* Hamburger Menu */}
            <button
              className="bg-coffee-800/80 backdrop-blur-sm p-2 rounded-full text-white"
              onClick={toggleMobileMenu}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              <span className="sr-only">{isMobileMenuOpen ? 'Close Menu' : 'Open Menu'}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Separate Mobile Menu (outside the header) */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-[100] bg-coffee-900/90 backdrop-blur-lg md:hidden flex flex-col"
          id="mobile-menu"
        >
          <div className="w-full px-4 py-6 flex justify-between items-center">
            <Link to="/" className="flex items-center">
              <Coffee className="h-8 w-8 text-coffee-300 mr-2" />
              <span className="text-xl font-serif font-semibold text-white">
                moca
              </span>
            </Link>
            <button
              className="bg-coffee-200/20 p-2 rounded-full text-white"
              onClick={toggleMobileMenu}
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>
          
          <div className="flex-1 flex flex-col items-center justify-center px-4 py-10 overflow-y-auto">
            <div className="space-y-6 w-full max-w-md">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`block text-lg font-sans font-medium w-full text-center py-3 rounded-full transition-all duration-300 ${
                    location.pathname === link.href
                      ? 'bg-coffee-600 text-white'
                      : 'text-coffee-200 hover:bg-coffee-800 hover:text-white'
                  }`}
                  onClick={toggleMobileMenu}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/menu"
                className="block w-full bg-coffee-600 text-white text-center py-4 rounded-full font-sans font-medium transition-all duration-300 hover:bg-coffee-700 flex items-center justify-center space-x-1"
                onClick={toggleMobileMenu}
              >
                <span>Shop Now</span>
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;